import { Edit01, Key01, Trash01 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { ButtonList } from '@/lib/base/button-list';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import type { CrmObject } from '@/types/crm';

import {
  credentialReachOf,
  describeScope,
  getCredentialName,
} from './api-access-service';
import { CREDENTIAL_NOUN, useDeleteCredential } from './use-delete-credential';

// Row-action icon buttons, matching the settings-list treatment used by
// pipelines-section.tsx so every settings row reads the same way. Styled off
// both `disabled:` (busy state, native disabled) and `aria-disabled:` (the
// !canWrite gate below, which deliberately avoids native disabled so the
// button stays focusable/hoverable and its tooltip can explain why).
const ICON_BUTTON_CLASSES =
  'p-1.5 rounded-md text-fg-quaternary hover:text-fg-primary hover:bg-bg-tertiary transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2';
const DESTRUCTIVE_ICON_BUTTON_CLASSES =
  'p-1.5 rounded-md text-fg-quaternary hover:text-error-primary hover:bg-error-secondary transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2';

interface MintTokenButtonProps {
  name: string;
  canWrite: boolean;
  isMinting: boolean;
  onConfirm: () => void;
}

function MintTokenButton({
  name,
  canWrite,
  isMinting,
  onConfirm,
}: MintTokenButtonProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const label = `New token for ${name}`;
  // aria-disabled (not native `isDisabled`) so the row's action stays
  // focusable/hoverable while blocked, and its tooltip can explain why —
  // disabled-not-hidden, matching the row's Add-button gating pattern.
  const isActionable = canWrite && !isMinting;

  const handleOpenConfirm = useCallback(() => {
    if (!isActionable) {
      return;
    }
    setIsConfirmOpen(true);
  }, [isActionable]);

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    setIsConfirmOpen(false);
    onConfirm();
  }, [onConfirm]);

  return (
    <>
      <Tooltip
        title={canWrite ? 'New token' : 'Only admins can create tokens'}
        // interactive: react-aria's non-interactive Tooltip ties its hover
        // state to the trigger's own disabled state, so it won't surface on
        // an aria-disabled (not natively disabled) button — see
        // Dropdown.Item's identical disabledTooltip comment. Always on (not
        // conditioned on canWrite) so the tree shape never changes.
        interactive
      >
        <TooltipTrigger
          aria-label={label}
          className={ICON_BUTTON_CLASSES}
          onPress={handleOpenConfirm}
          aria-disabled={!isActionable || undefined}
        >
          <Key01 className='h-4 w-4' />
        </TooltipTrigger>
      </Tooltip>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        title={`Create a new token for ${name}?`}
        description={`The token is shown once. Existing tokens for this ${CREDENTIAL_NOUN} keep working.`}
        confirmLabel='Create token'
        onConfirm={handleConfirm}
        onCancel={handleCloseConfirm}
        onClose={handleCloseConfirm}
        instance='mint-api-credential-token'
      />
    </>
  );
}

interface DeleteCredentialButtonProps {
  orgId: number;
  credentialEntityId: string;
  entityTypeName: string;
  canWrite: boolean;
  lastEventSeq: number;
  onDeleted: () => void;
}

function DeleteCredentialButton({
  orgId,
  credentialEntityId,
  entityTypeName,
  canWrite,
  lastEventSeq,
  onDeleted,
}: DeleteCredentialButtonProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { isDeleting, deleteWithSecrets } = useDeleteCredential({
    orgId,
    credentialEntityId,
    entityTypeName,
    lastEventSeq,
    onDeleted,
  });

  const deleteTitle = `Delete ${CREDENTIAL_NOUN}?`;
  const deleteDescription = `This ${CREDENTIAL_NOUN} and its tokens stop working immediately.`;
  const deleteLabel = `Delete ${CREDENTIAL_NOUN}`;
  const tooltipTitle = canWrite
    ? deleteLabel
    : 'Only admins can delete service accounts';
  // aria-disabled (not native `isDisabled`) so the row's action stays
  // focusable/hoverable while blocked, and its tooltip can explain why —
  // disabled-not-hidden, matching the row's Add-button gating pattern.
  const isActionable = canWrite && !isDeleting;

  const handleOpenConfirm = useCallback(() => {
    if (!isActionable) {
      return;
    }
    setIsConfirmOpen(true);
  }, [isActionable]);

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    await deleteWithSecrets();
  }, [deleteWithSecrets]);

  return (
    <>
      {/* interactive: see MintTokenButton's Tooltip above. */}
      <Tooltip title={tooltipTitle} interactive>
        <TooltipTrigger
          aria-label={deleteLabel}
          className={DESTRUCTIVE_ICON_BUTTON_CLASSES}
          onPress={handleOpenConfirm}
          aria-disabled={!isActionable || undefined}
        >
          <Trash01 className='h-4 w-4' />
        </TooltipTrigger>
      </Tooltip>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        title={deleteTitle}
        description={deleteDescription}
        variant='destructive'
        confirmLabel='Delete'
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseConfirm}
        onClose={handleCloseConfirm}
        instance='delete-api-credential'
      />
    </>
  );
}

export interface ApiAccessListItemProps {
  credential: CrmObject;
  orgId: number;
  canWrite: boolean;
  onRowClick: (id: string) => void;
  onDeleted: () => void;
  /** Mints an additional bearer token for this credential (see use-mint-token.ts). */
  onMintToken?: (credential: CrmObject) => void;
  /** True while a token is being minted for THIS row; disables its New token action. */
  isMintingToken?: boolean;
}

export function ApiAccessListItem({
  credential,
  orgId,
  canWrite,
  onRowClick,
  onDeleted,
  onMintToken,
  isMintingToken = false,
}: ApiAccessListItemProps) {
  const name = getCredentialName(credential);
  const isOrgReach = credentialReachOf(credential) === 'org';

  const createdAt = credential.properties?.['ahoy_created_at']?.value as
    | string
    | undefined;
  const rawPermissions = credential.properties?.['ahoy_permissions']?.value;
  const summary = useMemo(() => {
    const parts = [describeScope(rawPermissions)];
    if (createdAt) {
      parts.push(`Created ${formatRelativeTime(createdAt)}`);
    }
    return parts.join(' · ');
  }, [rawPermissions, createdAt]);

  const handleRowAction = useCallback(() => {
    onRowClick(credential.entity_id);
  }, [onRowClick, credential.entity_id]);

  const handleMintToken = useCallback(() => {
    onMintToken?.(credential);
  }, [onMintToken, credential]);

  return (
    <ButtonList.Item
      onAction={handleRowAction}
      showChevron
      aria-label={`Open ${name} details`}
      trailing={
        <div className='flex items-center gap-1'>
          {onMintToken && (
            <MintTokenButton
              name={name}
              canWrite={canWrite}
              isMinting={isMintingToken}
              onConfirm={handleMintToken}
            />
          )}
          <Tooltip title='Edit credential'>
            <TooltipTrigger
              aria-label={`Edit ${name}`}
              className={ICON_BUTTON_CLASSES}
              onPress={handleRowAction}
            >
              <Edit01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>
          <DeleteCredentialButton
            orgId={orgId}
            credentialEntityId={credential.entity_id}
            entityTypeName={credential.entity_type_name}
            canWrite={canWrite}
            lastEventSeq={credential.last_event_seq}
            onDeleted={onDeleted}
          />
        </div>
      }
    >
      <div className='flex min-w-0 items-center gap-2'>
        <span className='truncate'>{name}</span>
        {isOrgReach && (
          <Badge color='warning' size='sm'>
            Whole org
          </Badge>
        )}
      </div>
      <p className='mt-0.5 truncate text-xs text-fg-tertiary'>{summary}</p>
    </ButtonList.Item>
  );
}
